"use client";

import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { useTranslation } from "@/src/i18n/client";
import { AuthContext } from "@/src/components/AuthContextProvider";
import { BuildingsIcon, CaretDownIcon, GearIcon, GlobeIcon, SignOutIcon, UserCircleIcon, UserIcon } from "@phosphor-icons/react";
import { Button } from '../ui/button';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { AVAILABLE_LANGUAGES, UI_LANGUAGES } from '@/src/lib/availableLanguages'
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateMyPreferences } from '@/src/api/user';


export function Header() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const location = useLocation();
  const pathname = location.pathname;
  const [isSticky, setIsSticky] = useState(true);
  const auth = useContext(AuthContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const pricingEnabled = useFeatureFlagEnabled('pricing-option');
  const menuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const nestedLangMenuRef = useRef<HTMLDivElement>(null);

  const updateLocaleMutation = useMutation({
    mutationFn: async (newLocale: string) => {
      await i18n.changeLanguage(newLocale);
      return updateMyPreferences({ data: { locale: newLocale } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 60) {
        setIsSticky(false);
      } else {
        setIsSticky(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Call handler once on mount to set initial state based on current scroll position
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
        setLangMenuOpen(false); // Also close nested language menu
      }
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setLangMenuOpen(false);
      }
      // Handle nested language menu inside user menu
      if (nestedLangMenuRef.current && !nestedLangMenuRef.current.contains(event.target as Node) && !userMenuRef.current?.contains(event.target as Node)) {
        setLangMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header
      className={`w-full z-50 pt-[env(safe-area-inset-top)] ${pathname === '/' ? 'bg-transparent' : 'bg-background'} transition-all duration-300 ease-in-out ${isSticky ? 'sticky top-0 translate-y-0' : 'sticky top-0 -translate-y-full'
        }`}
    >
      <div className="flex h-12 w-full items-center space-x-4 sm:justify-between sm:space-x-0">
        <div className="flex-1 flex items-center justify-start">
          <Link to="/" className="flex items-center gap-2 text-3xl font-bold text-foreground pl-2">
            <img src="/favicon.svg" alt="GearLens" className="w-7 h-7 dark:invert" />
            <span className="hidden md:inline">GearLens</span>
          </Link>
        </div>
        <div className="flex flex-1 items-center justify-end space-x-4">
          <nav className="flex items-center space-x-1">
            {/* Collapsed menu for small screens */}
            <div className="relative sm:hidden" ref={menuRef}>
              <button
                className="flex items-center text-sm text-muted-foreground hover:underline px-2 py-1 rounded focus:outline-none"
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={menuOpen}
              >
                {t('header.menu')} <CaretDownIcon className="ml-1 w-4 h-4" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-background border border-border rounded shadow-lg z-50">
                  <Link to="/" className="block px-4 py-2 text-sm text-foreground hover:bg-muted" onClick={() => setMenuOpen(false)}>{t('header.upload')}</Link>
                  {auth?.isAuthenticated && <Link to="/create" className="block px-4 py-2 text-sm text-foreground hover:bg-muted" onClick={() => setMenuOpen(false)}>{t('header.create')}</Link>}
                  {pricingEnabled && <Link to="/pricing" className="block px-4 py-2 text-sm text-foreground hover:bg-muted" onClick={() => setMenuOpen(false)}>{t('header.pricing')}</Link>}
                  <Link to="/contact" className="block px-4 py-2 text-sm text-foreground hover:bg-muted" onClick={() => setMenuOpen(false)}>{t('header.contact')}</Link>
                  <Link to="/faq" className="block px-4 py-2 text-sm text-foreground hover:bg-muted" onClick={() => setMenuOpen(false)}>{t('header.faq')}</Link>
                  <Link to="/docs" className="block px-4 py-2 text-sm text-foreground hover:bg-muted" onClick={() => setMenuOpen(false)}>{t('header.docs')}</Link>
                </div>
              )}
            </div>
            {/* Inline links for larger screens */}
            <nav className="hidden sm:flex items-center space-x-1">
              <Link to="/" className="px-4 py-2 rounded hover:bg-muted text-sm font-medium transition-all duration-100 h-8 flex items-center">{t('header.upload')}</Link>
              {auth?.isAuthenticated && <Link to="/create" className="px-4 py-2 rounded hover:bg-muted text-sm font-medium transition-all duration-100 h-8 flex items-center">{t('header.create')}</Link>}
              {pricingEnabled && <Link to="/pricing" className="px-4 py-2 rounded hover:bg-muted text-sm font-medium transition-all duration-100 h-8 flex items-center">{t('header.pricing')}</Link>}
              <Link to="/contact" className="px-4 py-2 rounded hover:bg-muted text-sm font-medium transition-all duration-100 h-8 flex items-center">{t('header.contact')}</Link>
              <Link to="/faq" className="px-4 py-2 rounded hover:bg-muted text-sm font-medium transition-all duration-100 h-8 flex items-center">{t('header.faq')}</Link>
              <Link to="/docs" className="px-4 py-2 rounded hover:bg-muted text-sm font-medium transition-all duration-100 h-8 flex items-center">{t('header.docs')}</Link>
            </nav>
            {/* Manage/User menu or Login button */}
            {auth?.isAuthenticated ? (
              <div className="flex items-center gap-2">
                <Button
                  nativeButton={false}
                  render={<Link to="/manage" />}
                  type="button"
                  className="rounded bg-muted px-4 text-sm font-medium text-foreground hover:bg-muted/70 focus:outline-none focus:ring-4 focus:ring-border h-8 flex items-center justify-center border-0"
                >
                  {t('header.manage')}
                </Button>
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center justify-center rounded-full bg-muted w-8 h-8 text-foreground hover:bg-muted/70 focus:outline-none focus:ring-4 focus:ring-border overflow-hidden"
                    aria-haspopup="true"
                    aria-expanded={userMenuOpen}
                    aria-label="User menu"
                  >
                    {auth.user?.image ? (
                      <img src={auth.user.image} alt={auth.user.name || 'User'} className="w-full h-full object-cover" />
                    ) : (
                      <UserCircleIcon className="w-5 h-5" weight="fill" />
                    )}
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-52 bg-background border border-border rounded shadow-lg z-100">
                      {auth.user?.organization && (
                        <Link
                          to="/upload"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <BuildingsIcon className="w-4 h-4" />
                          {auth.user.organization.name}
                        </Link>
                      )}
                      {/* Language Selector */}
                      <div className="relative" ref={nestedLangMenuRef}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setLangMenuOpen(!langMenuOpen);
                          }}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-foreground hover:bg-muted text-left"
                        >
                          <GlobeIcon className="w-4 h-4" />
                          {t('header.language')}
                          <CaretDownIcon className="ml-auto w-3 h-3" />
                        </button>
                        {langMenuOpen && (
                          <div className="absolute right-full top-0 mr-1 w-40 bg-background border border-border rounded shadow-lg z-110">
                            {/* Render header languages from central config */}
                            {UI_LANGUAGES.map((code) => {
                              const lang = AVAILABLE_LANGUAGES.find((l) => l.code === code)!;
                              const active = i18n.language?.split('-')[0] === code;
                              return (
                                <button
                                  key={code}
                                  onClick={() => {
                                    updateLocaleMutation.mutate(code);
                                    setLangMenuOpen(false);
                                    setUserMenuOpen(false);
                                  }}
                                  className={`w-full px-4 py-2 text-sm text-left hover:bg-muted ${active ? 'bg-muted font-medium' : ''}`}
                                >
                                  {t(lang.nameKey)}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <Link
                        to="/manage/account"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <UserIcon className="w-4 h-4" />
                        {t('header.account')}
                      </Link>
                      <Link
                        to="/manage/account"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <GearIcon className="w-4 h-4" />
                        {t('header.settings')}
                      </Link>
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          auth.signOut();
                        }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-sm text-foreground hover:bg-muted text-left border-t border-border"
                      >
                        <SignOutIcon className="w-4 h-4" />
                        {t('header.logout')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2" ref={langMenuRef}>
                <div className="relative">
                  <button
                    onClick={() => setLangMenuOpen(!langMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded hover:bg-muted text-sm text-foreground focus:outline-none h-8"
                    aria-haspopup="true"
                    aria-expanded={langMenuOpen}
                    aria-label="Language selector"
                  >
                    <GlobeIcon className="w-4 h-4" />
                    <CaretDownIcon className="w-3 h-3" />
                  </button>
                  {langMenuOpen && (
                    <div className="absolute right-0 mt-2 w-40 bg-background border border-border rounded shadow-lg z-50">
                      {UI_LANGUAGES.map((code) => {
                        const lang = AVAILABLE_LANGUAGES.find((l) => l.code === code)!;
                        const active = i18n.language?.split('-')[0] === code;
                        return (
                          <button
                            key={code}
                            onClick={async () => {
                              await i18n.changeLanguage(code);
                              setLangMenuOpen(false);
                            }}
                            className={`w-full px-4 py-2 text-sm text-left hover:bg-muted ${active ? 'bg-muted font-medium' : ''}`}
                          >
                            {t(lang.nameKey)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <Button
                  nativeButton={false}
                  render={<Link to="/login" />}
                  type="button"
                  className="rounded bg-muted px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/70 focus:outline-none focus:ring-4 focus:ring-border h-8 flex items-center justify-center"
                  style={{ width: 'calc(4rem + 2.5rem + 0.5rem)' }}
                >
                  {t('header.login')}
                </Button>
              </div>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}
